import React, { createContext, useContext } from 'react';

/**
 * Design Template Context
 *
 * 모든 페이지에서 일관된 디자인 템플릿 데이터를 제공합니다.
 * 관리자에서 로고 수정 시 새로고침만으로 모든 페이지에 즉시 반영됩니다.
 */
const DesignTemplateContext = createContext(null);

/**
 * useDesignTemplate Hook
 *
 * 디자인 템플릿 데이터에 접근하기 위한 커스텀 훅
 *
 * @returns {Object} designTemplate - 현재 도메인의 디자인 템플릿 데이터
 * @throws {Error} Provider 외부에서 사용 시 에러 발생
 *
 * @example
 * const LoginPage = () => {
 *   const designTemplate = useDesignTemplate();
 *   return <img src={designTemplate?.unified_logo_url} />;
 * };
 */
export const useDesignTemplate = () => {
  const context = useContext(DesignTemplateContext);

  // Context가 Provider 내부에서 사용되는지 확인
  if (context === undefined) {
    throw new Error(
      'useDesignTemplate must be used within a DesignTemplateProvider. ' +
      'Make sure your component is wrapped with <DesignTemplateProvider>.'
    );
  }

  return context;
};

/**
 * DesignTemplateProvider Component
 *
 * 앱 전체에 디자인 템플릿 데이터를 제공하는 Provider 컴포넌트
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - 자식 컴포넌트
 * @param {Object} props.value - 제공할 디자인 템플릿 데이터
 *
 * @example
 * <DesignTemplateProvider value={designTemplate}>
 *   <App />
 * </DesignTemplateProvider>
 */
export const DesignTemplateProvider = ({ children, value }) => {
  return (
    <DesignTemplateContext.Provider value={value}>
      {children}
    </DesignTemplateContext.Provider>
  );
};

export default DesignTemplateContext;
